import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'compact' | 'horizontal';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  const hotBadge: Record<string, { text: string; className: string }> = {
    explosive: { text: '爆', className: 'bg-primary-500 text-background-50' },
    hot: { text: '热', className: 'bg-accent-500 text-background-50' },
    new: { text: '新', className: 'bg-secondary-500 text-background-50' },
  };

  const badge = hotBadge[article.hotLevel];

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex-shrink-0 w-[260px] rounded-xl overflow-hidden bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
      >
        <div className="relative w-full h-44 overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          <span className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-bold ${badge.className}`}>
            {badge.text}
          </span>
        </div>
        <div className="p-3">
          <p className="text-xs text-foreground-500 mb-1">{article.category}</p>
          <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 p-4 rounded-xl bg-background-50 hover:bg-background-100 transition-colors cursor-pointer"
      >
        <div className="relative w-40 h-28 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          <span className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-bold ${badge.className}`}>
            {badge.text}
          </span>
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <p className="text-xs text-foreground-500 mb-1">{article.category} · {article.publishDate}</p>
          <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <p className="text-xs text-foreground-500 mt-1.5 line-clamp-1">{article.excerpt}</p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group rounded-xl overflow-hidden bg-background-50 hover:bg-background-100 transition-all duration-300 cursor-pointer"
    >
      <div className="relative w-full aspect-[16/10] overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          title={article.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        <span className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-bold ${badge.className}`}>
          {badge.text}
        </span>
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="text-sm md:text-base font-medium text-white line-clamp-2 leading-snug">
            {article.title}
          </h3>
        </div>
      </div>
      <div className="p-4">
        <p className="text-xs text-foreground-600 mb-2">{article.category} · {article.publishDate}</p>
        <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">{article.excerpt}</p>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {article.tags.map((tag) => (
            <span
              key={tag}
              className="px-2 py-0.5 rounded-md bg-background-100 text-foreground-600 text-xs"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}